import { PrimeMark } from '@/components/PrimeMark'
import { useEffect, useState } from 'react'
import { api, engine } from '@/runtime'
import { useNames, useSuggestions } from '@/db/hooks'
import type { SuggestionResource } from '@/kinds/suggestion'
import { acceptSuggestion, describeOp, dismissSuggestion, refineSuggestion } from '@/lib/suggestions'
import { Button, IconButton, cx } from './ui'
import { Link, useNavigate } from '@tanstack/react-router'
import { ChevronDown } from 'lucide-react'
import { useAllPages } from '@/db/hooks'

const TYPE_ICON: Record<string, string> = {
  plan: '🧭',
  triage: '📥',
  schedule: '📅',
  breakdown: '🪜',
  answer: '💬',
  capture: '📥',
}

/** One suggestion: every op is a line you can untick; accept, refine or drop it. */
export const SuggestionCard = ({
  s,
  titleOf,
}: {
  s: SuggestionResource
  titleOf: (id: string) => string | undefined
}) => {
  const { kindOf } = useNames()
  const [kept, setKept] = useState<Set<number>>(() => new Set(s.spec.ops.map((_, i) => i)))
  const [refining, setRefining] = useState(false)
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<string | null>(null)
  const toggle = (i: number) =>
    setKept((k) => {
      const n = new Set(k)
      if (n.has(i)) n.delete(i)
      else n.add(i)
      return n
    })
  const accept = () =>
    acceptSuggestion(
      s,
      [...kept].sort((a, b) => a - b),
    )
  const refine = async () => {
    if (!note.trim()) return
    setBusy(true)
    setMsg(null)
    try {
      const n = await refineSuggestion(s, note.trim())
      await engine.sync()
      setMsg(n ? 'Revised.' : 'No change suggested.')
      setRefining(false)
      setNote('')
    } catch (e) {
      setMsg((e as Error).message)
    } finally {
      setBusy(false)
    }
  }
  const hasOps = s.spec.ops.length > 0
  if (s.status.state !== 'pending') return <DecidedCard s={s} titleOf={titleOf} />
  return (
    <li className="card px-3 py-2.5 !bg-accent-soft !border-transparent" data-suggestion-id={s.id}>
      <div className="flex items-start gap-2">
        <span className="text-lg leading-6">{TYPE_ICON[s.spec.type] ?? '✨'}</span>
        <div className="flex-1 min-w-0">
          <div className="font-medium leading-5">{s.spec.title}</div>
          {s.spec.rationale && <p className="text-sm text-fg-2 mt-0.5">{s.spec.rationale}</p>}
          {s.spec.feedback && (
            <p className="text-xs text-faint mt-0.5">after your note: “{s.spec.feedback}”</p>
          )}
        </div>
      </div>
      {hasOps && (
        <ul className="mt-2 flex flex-col gap-0.5" data-testid="suggestion-ops">
          {s.spec.ops.map((op, i) => (
            <li key={i}>
              <label
                className={cx(
                  'flex items-start gap-2 min-h-9 text-sm rounded px-1',
                  !kept.has(i) && 'text-faint line-through',
                )}
              >
                <input
                  type="checkbox"
                  checked={kept.has(i)}
                  onChange={() => toggle(i)}
                  className="mt-2 h-4 w-4"
                  aria-label={`keep: ${describeOp(op, titleOf, kindOf)}`}
                />
                <span className="py-2">{describeOp(op, titleOf, kindOf)}</span>
              </label>
            </li>
          ))}
        </ul>
      )}
      {refining && (
        <div className="mt-2 flex gap-2 items-start fade-in">
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            className="flex-1 text-sm"
            placeholder="What would you change? e.g. “not the dentist one, and make them shorter”"
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                void refine()
              }
            }}
          />
          <Button variant="primary" onClick={() => void refine()} disabled={busy || !note.trim()}>
            {busy ? '…' : 'Send'}
          </Button>
        </div>
      )}
      {msg && <p className="text-xs text-muted mt-1">{msg}</p>}
      <div className="flex flex-wrap gap-2 justify-end mt-2">
        <Button onClick={() => void dismissSuggestion(s)}>Not now</Button>
        <Button onClick={() => setRefining((r) => !r)} data-testid="suggestion-refine">
          {refining ? 'Cancel' : 'Refine'}
        </Button>
        {hasOps ? (
          <Button
            variant="primary"
            onClick={() => void accept()}
            disabled={kept.size === 0}
            data-testid="suggestion-accept"
          >
            {/* one vocabulary on every waiting card: Do it, Not now */}
            {kept.size < s.spec.ops.length ? `Do ${kept.size} of ${s.spec.ops.length}` : 'Do it'}
          </Button>
        ) : (
          <Button variant="primary" onClick={() => void dismissSuggestion(s)}>
            OK
          </Button>
        )}
      </div>
    </li>
  )
}

const routeFor = (kind: string): '/pages/$id' | '/tasks/$id' | '/projects/$id' | '/lists/$id' | undefined =>
  kind === 'Page'
    ? '/pages/$id'
    : kind === 'Task'
      ? '/tasks/$id'
      : kind === 'Project'
        ? '/projects/$id'
        : kind === 'List'
          ? '/lists/$id'
          : undefined

/** A suggestion after the decision: what happened, with links to anything it created. */
const DecidedCard = ({
  s,
  titleOf,
}: {
  s: SuggestionResource
  titleOf: (id: string) => string | undefined
}) => {
  const pages = useAllPages() ?? []
  const created = s.status.created ?? []
  const label =
    s.status.state === 'accepted'
      ? s.status.accepted_ops && s.status.accepted_ops.length < s.spec.ops.length
        ? `Accepted ${s.status.accepted_ops.length} of ${s.spec.ops.length}`
        : 'Accepted'
      : s.status.state === 'dismissed'
        ? 'Not now'
        : s.status.state === 'superseded'
          ? 'Revised'
          : s.status.state === 'applied'
            ? 'Filed for you'
            : s.status.state === 'reverted'
              ? 'Put back'
              : 'Expired'
  const nameOf = (c: { kind: string; id: string }) =>
    (c.kind === 'Page' ? pages.find((p) => p.id === c.id)?.name : titleOf(c.id)) ??
    (s.spec.ops[created.findIndex((x) => x.id === c.id)]?.spec?.title as string | undefined) ??
    c.kind
  return (
    <li
      className="rounded-xl px-3 py-2 bg-panel/60 text-sm"
      data-suggestion-id={s.id}
      data-state={s.status.state}
    >
      <div className="flex items-center gap-2 text-muted">
        <span>{TYPE_ICON[s.spec.type] ?? '✨'}</span>
        {s.spec.type === 'capture' ? (
          <Link
            to="/captures/$id"
            params={{ id: s.id }}
            className="truncate underline-offset-2 hover:underline"
          >
            {s.spec.title}
          </Link>
        ) : (
          <span className="truncate">{s.spec.title}</span>
        )}
        <span className="ml-auto text-xs text-faint shrink-0">{label}</span>
      </div>
      {created.length > 0 && (
        <ul className="mt-1.5 flex flex-wrap gap-1.5" data-testid="suggestion-created">
          {created.map((c) => {
            const to = routeFor(c.kind)
            const chip =
              'inline-flex items-center gap-1 rounded-full bg-raised px-2.5 py-1 text-xs hover:bg-line'
            return (
              <li key={c.id}>
                {to ? (
                  <Link to={to} params={{ id: c.id }} className={chip}>
                    {c.kind === 'Page' ? '📄' : c.kind === 'Project' ? '▣' : '○'} {nameOf(c)}
                  </Link>
                ) : (
                  <span className={chip}>{nameOf(c)}</span>
                )}
              </li>
            )
          })}
        </ul>
      )}
    </li>
  )
}

/** Up to three pending suggestions. Never auto-applies. */
/**
 * One on Today. This is a recommendation of what to do next, not a list to
 * work through: a screenful of cards is a screenful of decisions, which is the
 * opposite of the help being offered.
 */
const TODAY_SUGGESTIONS = 1

/**
 * A suggestion folded to its title: Today's version, so a four-step plan is
 * one line until you open it. Opening shows the full card with its ticks.
 */
const SuggestionTeaser = ({
  s,
  titleOf,
}: {
  s: SuggestionResource
  titleOf: (id: string) => string | undefined
}) => {
  const [open, setOpen] = useState(false)
  if (open || s.status.state !== 'pending') return <SuggestionCard s={s} titleOf={titleOf} />
  const n = s.spec.ops.length
  return (
    <li className="card !bg-accent-soft !border-transparent" data-suggestion-id={s.id}>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-expanded={false}
        className="w-full text-left flex items-start gap-2.5 px-3.5 py-3"
        data-testid="suggestion-open"
      >
        <span className="text-lg leading-6" aria-hidden>
          {TYPE_ICON[s.spec.type] ?? '✨'}
        </span>
        <span className="flex-1 min-w-0">
          <span className="block font-medium leading-5">{s.spec.title}</span>
          <span className="block text-xs text-muted mt-0.5">
            {n === 0 ? 'A thought' : n === 1 ? 'A change to look over' : 'Changes to look over'}
          </span>
        </span>
        <ChevronDown size={16} className="mt-1 shrink-0 text-muted" aria-hidden />
      </button>
    </li>
  )
}

export const SuggestionsRow = ({ types, compact = false }: { types?: string[]; compact?: boolean }) => {
  const all = useSuggestions()
  const { titleOf } = useNames()
  const [open, setOpen] = useState(false)
  const list = types ? all.filter((s) => types.includes(s.spec.type)) : all
  if (!list.length) return null
  const shown = open ? list : list.slice(0, TODAY_SUGGESTIONS)
  const hidden = list.length - shown.length
  const Item = compact ? SuggestionTeaser : SuggestionCard
  return (
    <section className={compact ? undefined : 'mb-3'} data-testid="suggestions">
      <h2 className="text-[13px] font-semibold text-muted mb-2">What I’d do next</h2>
      <div className={cx('relative', hidden > 0 && !compact && 'pb-6')}>
        <ul className="flex flex-col gap-1.5">
          {shown.map((s) => (
            <Item key={s.id} s={s} titleOf={titleOf} />
          ))}
          {/* a glimpse of the next one under the fade, so it is clear there is more */}
          {hidden > 0 && !compact && (
            <li className="max-h-10 overflow-hidden opacity-60" aria-hidden>
              <SuggestionCard s={list[TODAY_SUGGESTIONS]!} titleOf={titleOf} />
            </li>
          )}
        </ul>
        {hidden > 0 && !compact && (
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-ink via-ink/85 to-transparent" />
        )}
      </div>
      {hidden > 0 && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="mt-1 text-xs text-muted hover:text-fg min-h-9"
          data-testid="suggestions-more"
        >
          {hidden} other {hidden === 1 ? 'idea' : 'ideas'}
        </button>
      )}
      {open && list.length > TODAY_SUGGESTIONS && (
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="mt-1 text-xs text-faint hover:text-fg min-h-9"
          data-testid="suggestions-fewer"
        >
          Show fewer
        </button>
      )}
    </section>
  )
}

/** Whether the server has an assistant configured at all. */
export const useAssistantEnabled = (): boolean | null => {
  const [enabled, setEnabled] = useState<boolean | null>(null)
  useEffect(() => {
    api
      .config()
      .then((c) => setEnabled(!!c.assistant))
      .catch(() => setEnabled(false))
  }, [])
  return enabled
}

/**
 * Whether there is a Prime to ask: the factory (Prime runs there, lib/lead.ts)
 * or an assistant (the quick chat, from Prime's start screen).
 */
const usePrimeReachable = (): boolean => {
  const [on, setOn] = useState(false)
  useEffect(() => {
    api
      .config()
      .then((c) => setOn(!!c.assistant || !!c.factory))
      .catch(() => setOn(false))
  }, [])
  return on
}

/**
 * Ask is a way into the conversation, not a form: it opens Prime's thread
 * (or its start screen when it is not running).
 */
export const AskButton = () => {
  const enabled = usePrimeReachable()
  const navigate = useNavigate()
  if (!enabled) return null
  return (
    <Button variant="chip" onClick={() => void navigate({ to: '/agents' })} data-testid="ask-assistant">
      ✨ Ask
    </Button>
  )
}

/** The same way in as a small icon, for a row that already has its buttons (Today's tool row). */
export const AskIcon = () => {
  const enabled = usePrimeReachable()
  const navigate = useNavigate()
  if (!enabled) return null
  return (
    <IconButton
      label="Ask Prime"
      onClick={() => void navigate({ to: '/agents' })}
      className="!min-h-8 !min-w-8 text-accent-text"
      data-testid="ask-assistant"
    >
      <PrimeMark size={15} aria-hidden />
    </IconButton>
  )
}
